import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PimpinanLayout from "../../components/layout/PimpinanLayout";
import { useAuth } from "../../context/AuthContext"; // sesuaikan path

const HARI_ORDER = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu"];

export default function Jadwal() {
  const { token } = useAuth(); // ← pakai session token

  const [jadwal, setJadwal] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [selectedKelas, setSelectedKelas] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const formatJam = (jam) => {
    if (!jam) return "";
    return jam.slice(0, 5).replace(":", ".");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jadwalRes, kelasRes] = await Promise.all([
          fetch("https://backend.smpn1kamal.sch.id/api/jadwal", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch("https://backend.smpn1kamal.sch.id/api/kelas", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const jadwalData = await jadwalRes.json();
        const kelasData = await kelasRes.json();

        setJadwal(jadwalData);
        setKelasList(kelasData);

        const defaultKelas =
          kelasData.find((k) => k.nama_kelas === "7A") ||
          kelasData[0];

        if (defaultKelas) {
          setSelectedKelas(defaultKelas.id_kelas);
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const jadwalFiltered = selectedKelas
    ? jadwal.filter((j) => {
        const matchKelas = Number(j.id_kelas) === Number(selectedKelas);
        const keyword = search.toLowerCase();
        const matchSearch =
          j.nama_mapel?.toLowerCase().includes(keyword) ||
          j.nama_lengkap?.toLowerCase().includes(keyword);
        return matchKelas && matchSearch;
      })
    : [];

  const sortedJadwal = [...jadwalFiltered].sort((a, b) => {
    const d = HARI_ORDER.indexOf(a.hari) - HARI_ORDER.indexOf(b.hari);
    return d !== 0 ? d : a.jam_mulai.localeCompare(b.jam_mulai);
  });

  const downloadPDF = () => {
    const kelasAktif = kelasList.find(
      (k) => Number(k.id_kelas) === Number(selectedKelas)
    );
    const kelasNama = kelasAktif?.nama_kelas || "";

    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;

    doc.addImage("/logo.png", "PNG", margin, 12, 18, 18);

    doc.setFont("times", "bold");
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text("UPTD SMPN 1 KAMAL", pageW / 2, 16, { align: "center" });

    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.text("Jadwal Pelajaran", pageW / 2, 22, { align: "center" });

    doc.setFont("times", "bold");
    doc.setFontSize(13);
    doc.text(`Kelas ${kelasNama}`, pageW / 2, 28, { align: "center" });

    const now = new Date();
    const y = now.getFullYear();
    const tahunAjaran = now.getMonth() >= 6 ? `${y}/${y + 1}` : `${y - 1}/${y}`;

    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.text(`Tahun Pelajaran ${tahunAjaran}`, pageW / 2, 34, { align: "center" });

    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.7);
    doc.line(margin, 38, pageW - margin, 38);

    const rows = [];
    HARI_ORDER.forEach((hari) => {
      const dataHari = sortedJadwal.filter(
        (j) => j.hari?.toLowerCase() === hari.toLowerCase()
      );
      if (dataHari.length === 0) return;
      dataHari.forEach((item, idx) => {
        rows.push({
          isFirst: idx === 0,
          hariLabel: hari,
          mapel: item.nama_mapel,
          guru: item.nama_lengkap,
          jamMulai: formatJam(item.jam_mulai),
          jamSelesai: formatJam(item.jam_selesai),
        });
      });
    });

    autoTable(doc, {
      startY: 42,
      margin: { left: margin, right: margin },
      head: [["Hari", "Waktu", "Mata Pelajaran", "Guru"]],
      body: rows.map((r) => [
        r.isFirst ? r.hariLabel : "",
        `${r.jamMulai} – ${r.jamSelesai}`,
        r.mapel,
        r.guru,
      ]),
      styles: {
        font: "times",
        fontSize: 10,
        cellPadding: 4,
        textColor: [0, 0, 0],
        lineWidth: 0,
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [240, 244, 255] },
      columnStyles: {
        0: { cellWidth: 25, fontStyle: "bold" },
        1: { cellWidth: 35, halign: "center" },
        2: { cellWidth: 65 },
        3: { cellWidth: 45 },
      },
      didDrawCell(data) {
        if (data.section !== "body") return;
        const rowIdx = data.row.index;
        if (rowIdx === 0) return;
        const curr = rows[rowIdx];
        if (curr?.isFirst) {
          doc.setDrawColor(37, 99, 235);
          doc.setLineWidth(0.4);
          doc.line(margin, data.cell.y, pageW - margin, data.cell.y);
        }
      },
    });

    const footerY = pageH - 10;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY - 4, pageW - margin, footerY - 4);

    doc.setFont("times", "normal");
    doc.setFontSize(9);
    doc.text(
      `Dicetak: ${now.toLocaleDateString("id-ID", {
        day: "2-digit", month: "long", year: "numeric",
      })}`,
      margin,
      footerY
    );
    doc.text("SIPENSA — UPTD SMPN 1 Kamal", pageW - margin, footerY, {
      align: "right",
    });

    doc.save(`Jadwal_Kelas_${kelasNama}.pdf`);
  };

  return (
    <PimpinanLayout>

      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-bold text-blue-600 truncate">
            Jadwal Pembelajaran
          </h2>
          <p className="text-xs md:text-sm text-slate-500">
            SMPN 1 Kamal
          </p>
        </div>

        <button
          onClick={downloadPDF}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs md:text-sm px-4 py-2 rounded-full whitespace-nowrap"
        >
          Unduh Jadwal
        </button>
      </div>

      {/* CONTAINER */}
      <div className="bg-white rounded-2xl shadow-md p-4 md:p-5">

        {/* TOOLBAR */}
        <div className="flex flex-row items-center justify-between gap-3 mb-5">

          <select
            value={selectedKelas}
            onChange={(e) => setSelectedKelas(Number(e.target.value))}
            className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm w-32 md:w-40"
          >
            {kelasList.map((kelas) => (
              <option key={kelas.id_kelas} value={kelas.id_kelas}>
                {kelas.nama_kelas}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-slate-300 rounded-full px-3 py-2 text-xs md:text-sm w-40 md:w-72"
          />

        </div>

        {/* DESKTOP TABLE */}
        <div className="hidden md:block overflow-x-auto">
          {loading ? (
            <div className="text-center py-20 text-slate-500">
              Memuat jadwal...
            </div>
          ) : sortedJadwal.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              {search
                ? `Pencarian "${search}" tidak ditemukan`
                : "Jadwal belum tersedia"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left">HARI</th>
                  <th className="px-4 py-3 text-left">WAKTU</th>
                  <th className="px-4 py-3 text-left">MATA PELAJARAN</th>
                  <th className="px-4 py-3 text-left">GURU</th>
                </tr>
              </thead>
              <tbody>
                {sortedJadwal.map((item, i) => {
                  const isNewDay =
                    i === 0 || item.hari !== sortedJadwal[i - 1].hari;
                  return (
                    <tr key={item.id_jadwal}>
                      <td className="px-4 py-3 font-semibold">
                        {isNewDay ? item.hari : ""}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {formatJam(item.jam_mulai)} –{" "}
                        {formatJam(item.jam_selesai)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold">
                          {item.nama_mapel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {item.nama_lengkap}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* MOBILE CARD */}
        <div className="flex flex-col gap-4 md:hidden">
          {loading ? (
            <div className="text-center py-20 text-slate-500">
              Memuat jadwal...
            </div>
          ) : sortedJadwal.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              {search
                ? `Pencarian "${search}" tidak ditemukan`
                : "Jadwal belum tersedia"}
            </div>
          ) : (
            Object.entries(
              sortedJadwal.reduce((acc, item) => {
                if (!acc[item.hari]) acc[item.hari] = [];
                acc[item.hari].push(item);
                return acc;
              }, {})
            ).map(([hari, items]) => (
              <div key={hari}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-semibold text-blue-600 uppercase">
                    {hari}
                  </span>
                  <div className="flex-1 h-px bg-blue-100" />
                </div>

                <div className="flex flex-col gap-2">
                  {items.map((item) => (
                    <div
                      key={item.id_jadwal}
                      className="border rounded-xl p-4 shadow-sm"
                    >
                      <span className="bg-blue-100 text-blue-600 text-xs px-3 py-1 rounded-full font-semibold">
                        {item.nama_mapel}
                      </span>
                      <p className="text-sm text-slate-700 mt-1">
                        {item.nama_lengkap}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatJam(item.jam_mulai)} –{" "}
                        {formatJam(item.jam_selesai)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

      </div>
    </PimpinanLayout>
  );
}